import { Create, useForm, useSelect } from "@refinedev/antd";
import { DatePicker, Form, InputNumber, Select, Space, Tag } from "antd";
import { IResourceComponentsProps } from "@refinedev/core";
import { AppstoreOutlined, SettingOutlined } from "@ant-design/icons";

import { ICurrency, IMetadataOther, ISubscription } from "interfaces";
import { FormSection, FormFieldGrid } from "components/form";
import { PricingFields } from "components/metadata/PricingFields";
import { useAvcsForSubscription } from "components/metadata/useAvcsForSubscription";
import {
  defaultEnrollmentDate,
  enrollmentDateFieldProps,
} from "components/metadata/enrollmentDate";
import {
  OTHER_SERVICE_CATALOG,
  OTHER_SERVICES_BY_PRODUCT,
  OTHER_STATUS_OPTIONS,
  OTHER_SUBSCRIPTION_TYPES,
  VISIBLE_OTHER_SERVICE_CATALOG,
  WIFI_DONGLE_VISIBLE,
} from "components/metadata/otherServiceCatalog";

// Services present in the full catalog but not rendered as a visible Form.Item
// (e.g. standardLibraryPublicationsDigital). Computed as a set difference so any
// service hidden in the future is covered automatically, with no per-prefix special-casing.
const HIDDEN_OTHER_SERVICES = OTHER_SERVICE_CATALOG.filter(
  (svc) => !VISIBLE_OTHER_SERVICE_CATALOG.includes(svc),
);

const buildInitialValues = (): Record<string, unknown> =>
  OTHER_SERVICE_CATALOG.reduce<Record<string, unknown>>((acc, svc) => {
    acc[`${svc.prefix}Status`] = "inactive";
    acc[`${svc.prefix}EnrollmentDate`] = defaultEnrollmentDate();
    acc[`${svc.prefix}SubscriptionType`] = "Pay Per Use";
    acc[`${svc.prefix}AnnualFlatFeePerVessel`] = 0;
    acc[`${svc.prefix}Currency`] = "USD";
    if (svc.usesPayPerUseAmount) {
      acc[`${svc.prefix}PayPerUseAmount`] = 0;
    }
    if (svc.hasWifiDongle) {
      acc[`${svc.prefix}WifiDongleCurrency`] = "USD";
    }
    return acc;
  }, {});

export const MetadataOtherCreate: React.FC<IResourceComponentsProps> = () => {
  const {
    formProps,
    saveButtonProps,
    formLoading: isLoading,
  } = useForm<IMetadataOther>({ redirect: "list" });

  const { selectProps } = useSelect<ISubscription>({
    resource: "subscription",
    optionLabel: "subscriptionName",
    optionValue: "id",
    pagination: { mode: "server" },
  });

  const { selectProps: currencySelectProps } = useSelect<ICurrency>({
    resource: "currency",
    optionLabel: "code",
    optionValue: "code",
    pagination: { mode: "server" },
  });

  const selectedSubscriptionId = Form.useWatch<string | undefined>(
    ["subscription", "id"],
    formProps.form,
  );

  const {
    amount: avcsFlatFeeAmount,
    currency: avcsFlatFeeCurrency,
  } = useAvcsForSubscription(selectedSubscriptionId);

  return (
    <Create
      breadcrumb={false}
      saveButtonProps={saveButtonProps}
      headerProps={{
        onBack: () => window.history.back(),
        title: "Create Other Services",
        subTitle: "setup other services",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
      }}
    >
      <Form
        {...formProps}
        layout="vertical"
        initialValues={buildInitialValues()}
      >
        <Space direction="vertical" size={24} style={{ width: "100%" }}>
          <FormSection
            title="Subscription"
            subtitle="Link to active subscription"
            icon={<AppstoreOutlined />}
            variant="elevated"
          >
            <Form.Item
              label="Subscription Name"
              name={["subscription", "id"]}
              rules={[{ required: true }]}
            >
              <Select {...selectProps} />
            </Form.Item>
          </FormSection>

          {OTHER_SERVICES_BY_PRODUCT.map((group) => (
            <FormSection
              key={group.product}
              title={group.product}
              subtitle={`${group.services.length} service${group.services.length > 1 ? "s" : ""}`}
              icon={group.icon}
              variant="elevated"
            >
              <Space
                direction="vertical"
                size={16}
                style={{ width: "100%" }}
              >
                {group.services.map((svc) => (
                  <FormSection
                    key={svc.prefix}
                    title={svc.service}
                    subtitle={svc.subtitle}
                    icon={<SettingOutlined />}
                    variant="subtle"
                  >
                    <FormFieldGrid columns={2}>
                      <Form.Item
                        label="Status"
                        name={`${svc.prefix}Status`}
                        rules={[{ required: false }]}
                      >
                        <Select options={OTHER_STATUS_OPTIONS} />
                      </Form.Item>
                      <Form.Item
                        label="Enrollment Date"
                        name={`${svc.prefix}EnrollmentDate`}
                        rules={[{ required: true }]}
                        {...enrollmentDateFieldProps}
                      >
                        <DatePicker
                          format="DD/MM/YYYY"
                          style={{ width: "100%" }}
                        />
                      </Form.Item>
                      <Form.Item
                        label="Subscription Type"
                        name={`${svc.prefix}SubscriptionType`}
                        rules={[{ required: false }]}
                      >
                        <Select options={OTHER_SUBSCRIPTION_TYPES} />
                      </Form.Item>
                      <Form.Item
                        label="Currency"
                        name={`${svc.prefix}Currency`}
                        rules={[{ required: false }]}
                      >
                        <Select {...currencySelectProps} allowClear />
                      </Form.Item>
                      <PricingFields
                        svc={svc}
                        form={formProps.form}
                        avcsFlatFeeAmount={avcsFlatFeeAmount}
                        avcsFlatFeeCurrency={avcsFlatFeeCurrency}
                      />
                    </FormFieldGrid>
                    {svc.hasWifiDongle && WIFI_DONGLE_VISIBLE && (
                      <FormFieldGrid columns={2}>
                        <Form.Item
                          label="Wi-Fi Dongle One-Time Cost"
                          name={`${svc.prefix}WifiDongleOneTimeCost`}
                          rules={[{ required: false }]}
                        >
                          <InputNumber
                            min={0}
                            precision={2}
                            style={{ width: "100%" }}
                          />
                        </Form.Item>
                        <Form.Item
                          label="Wi-Fi Dongle Currency"
                          name={`${svc.prefix}WifiDongleCurrency`}
                          rules={[{ required: false }]}
                        >
                          <Select {...currencySelectProps} allowClear />
                        </Form.Item>
                      </FormFieldGrid>
                    )}
                  </FormSection>
                ))}
              </Space>
            </FormSection>
          ))}
        </Space>
        {/*
         * CreateMetadataOtherDto requires an EnrollmentDate for every one of the eight
         * other-services, including standardLibraryPublicationsDigital, which is
         * deliberately hidden from this form (see `hidden: true` in otherServiceCatalog.tsx).
         * antd/rc-field-form only submits values for fields registered via a Form.Item -
         * setting a value in `initialValues` alone is not enough. These hidden Form.Items
         * register the value already defaulted in buildInitialValues above, without
         * rendering anything, so the POST payload includes it and DTO validation passes.
         */}
        {HIDDEN_OTHER_SERVICES.map((svc) => (
          <Form.Item
            key={svc.prefix}
            hidden
            name={`${svc.prefix}EnrollmentDate`}
          >
            <input type="hidden" />
          </Form.Item>
        ))}
      </Form>
    </Create>
  );
};
